import React, { useMemo } from 'react';
import { Plus, Save, Trash2, UserRound, X } from 'lucide-react';
import SearchableSelect from 'components/common/SearchableSelect';

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const ModalMeta = ({
  isOpen,
  mesEdicao,
  periodoLabel,
  metaTemp,
  vendasAtuais,
  onMetaChange,
  onSalvar,
  onCancelar,
  vendedoresDisponiveis = [],
  loadingVendedores = false,
  metasVendedores = [],
  onMetasVendedoresChange,
}) => {
  const metaGeral = Number(metaTemp || 0);
  const totalDistribuido = useMemo(
    () => metasVendedores.reduce((sum, item) => sum + Number(item.meta_vendas || 0), 0),
    [metasVendedores]
  );
  const saldoDisponivel = Math.max(0, metaGeral - totalDistribuido);
  const excedeuMeta = totalDistribuido > metaGeral;
  const idsSelecionados = metasVendedores.map((item) => String(item.vendedor_id)).filter(Boolean);
  const possuiLinhaIncompleta = metasVendedores.some(
    (item) => !item.vendedor_id || !String(item.vendedor_nome || '').trim() || item.meta_vendas === '' || Number(item.meta_vendas) < 0
  );
  const possuiDuplicado = idsSelecionados.length !== new Set(idsSelecionados).size;
  const salvarDesabilitado = excedeuMeta || possuiLinhaIncompleta || possuiDuplicado;

  if (!isOpen) return null;

  const updateMetaVendedor = (index, patch) => {
    const next = metasVendedores.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    ));
    onMetasVendedoresChange?.(next);
  };

  const handleSelecionarVendedor = (index, vendedorId) => {
    const vendedor = vendedoresDisponiveis.find((item) => String(item.id) === String(vendedorId));
    updateMetaVendedor(index, {
      vendedor_id: vendedor?.id || '',
      vendedor_nome: vendedor?.nome || '',
    });
  };

  const adicionarVendedor = () => {
    onMetasVendedoresChange?.([
      ...metasVendedores,
      { vendedor_id: '', vendedor_nome: '', meta_vendas: '' },
    ]);
  };

  const removerVendedor = (index) => {
    onMetasVendedoresChange?.(metasVendedores.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[color:var(--surface)] rounded-2xl p-4 sm:p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-[color:var(--border)] shadow-2xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[color:var(--accent)] mb-1">
              Meta comercial
            </p>
            <h3 className="text-lg sm:text-2xl font-bold text-[color:var(--text-primary)]">
              Definir Meta - {periodoLabel || mesEdicao}
            </h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">
              Cadastre a meta geral e, se quiser, distribua parte dela por vendedor.
            </p>
          </div>
          <button
            onClick={onCancelar}
            className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] p-2 rounded-xl hover:bg-[color:var(--surface-muted)] transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
            aria-label="Fechar modal de meta"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
          <section className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Meta geral de contratos
              </label>
              <input
                type="number"
                value={metaTemp}
                onChange={onMetaChange}
                className="w-full px-3 py-3 text-base border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-xl focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] outline-none"
                placeholder="Digite a meta de contratos"
                min="0"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[color:var(--surface-muted)] p-3 rounded-xl border border-[color:var(--border)]">
                <div className="text-xs text-[color:var(--text-muted)]">Vendas atuais</div>
                <div className="text-xl font-bold text-[color:var(--accent)] mt-1">
                  {formatNumber(vendasAtuais)}
                </div>
              </div>
              <div className="bg-[color:var(--surface-muted)] p-3 rounded-xl border border-[color:var(--border)]">
                <div className="text-xs text-[color:var(--text-muted)]">Disponível</div>
                <div className={`text-xl font-bold mt-1 ${excedeuMeta ? 'text-[color:var(--status-bad)]' : 'text-[color:var(--status-good)]'}`}>
                  {formatNumber(saldoDisponivel)}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-[color:var(--text-muted)]">Distribuído</span>
                <span className="font-semibold text-[color:var(--text-primary)]">
                  {formatNumber(totalDistribuido)} / {formatNumber(metaGeral)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-black/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${excedeuMeta ? 'bg-[color:var(--status-bad)]' : 'bg-[color:var(--accent)]'}`}
                  style={{ width: `${Math.min(metaGeral > 0 ? (totalDistribuido / metaGeral) * 100 : 0, 100)}%` }}
                />
              </div>
              {excedeuMeta && (
                <p className="text-xs text-[color:var(--status-bad)] mt-3">
                  A soma das metas dos vendedores não pode ser maior que a meta geral.
                </p>
              )}
              {possuiDuplicado && (
                <p className="text-xs text-[color:var(--status-bad)] mt-3">
                  Remova vendedores duplicados antes de salvar.
                </p>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h4 className="text-sm font-bold text-[color:var(--text-primary)] flex items-center gap-2">
                  <UserRound className="h-4 w-4 text-[color:var(--accent)]" />
                  Distribuição por vendedor
                </h4>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  Opcional. Use para reservar parte da meta geral.
                </p>
              </div>
              <button
                type="button"
                onClick={adicionarVendedor}
                disabled={loadingVendedores}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[color:var(--accent)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              >
                <Plus className="h-4 w-4" />
                Adicionar
              </button>
            </div>

            {loadingVendedores ? (
              <div className="space-y-3">
                {[0, 1].map((item) => (
                  <div key={item} className="h-14 rounded-xl bg-[color:var(--surface)] animate-pulse" />
                ))}
              </div>
            ) : metasVendedores.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[color:var(--border)] p-6 text-center">
                <p className="text-sm font-medium text-[color:var(--text-primary)]">Nenhum vendedor definido</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  A meta geral será salva normalmente sem distribuição individual.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {metasVendedores.map((item, index) => {
                  const opcoesDisponiveis = vendedoresDisponiveis.filter((vendedor) => (
                    String(vendedor.id) === String(item.vendedor_id) || !idsSelecionados.includes(String(vendedor.id))
                  ));
                  const opcoesVendedores = opcoesDisponiveis.map((vendedor) => ({
                    id: vendedor.id,
                    label: vendedor.nome,
                  }));
                  const vendedorSelecionado = item.vendedor_id
                    ? {
                        id: item.vendedor_id,
                        label: item.vendedor_nome || 'Vendedor selecionado',
                      }
                    : null;

                  return (
                    <div key={`${item.vendedor_id || 'novo'}-${index}`} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_40px] gap-2">
                      <SearchableSelect
                        options={opcoesVendedores}
                        value={vendedorSelecionado}
                        onChange={(option) => handleSelecionarVendedor(index, option?.id || '')}
                        placeholder="Buscar vendedor"
                        loading={loadingVendedores}
                        searchFields={['label', 'id']}
                      />
                      <input
                        type="number"
                        min="0"
                        value={item.meta_vendas}
                        onChange={(event) => updateMetaVendedor(index, { meta_vendas: event.target.value })}
                        className="px-3 py-2.5 text-sm border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-xl focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] outline-none"
                        placeholder="Meta"
                      />
                      <button
                        type="button"
                        onClick={() => removerVendedor(index)}
                        className="inline-flex items-center justify-center h-10 rounded-xl text-[color:var(--text-muted)] hover:text-[color:var(--status-bad)] hover:bg-[color:var(--surface)] transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                        aria-label="Remover vendedor da meta"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            onClick={onCancelar}
            className="flex-1 px-4 py-3 text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-xl hover:opacity-90 transition-opacity text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
          >
            Cancelar
          </button>
          <button
            onClick={onSalvar}
            disabled={salvarDesabilitado}
            className="flex-1 px-4 py-3 bg-[color:var(--accent)] text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center space-x-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
          >
            <Save className="h-4 w-4" />
            <span>Salvar Meta</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMeta;
